import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronLeft, IconEdit, IconEye } from '@tabler/icons-react';
import { handleApiError } from 'api/error-handling';
import { WorkoutDetail } from 'components/Workout';
import { useAuth } from 'context/auth-context';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useEditAssignedWorkout from 'hooks/workout/useEditAssignedWorkout';
import useWorkout from 'hooks/workout/useWorkout';
import type { WorkoutTemplatePreviewData } from 'pages/private/owner/Workouts/Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview';
import { useNavigate } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

import {
  buildPreviewDataFromWorkout,
  buildWorkoutUpdateFromRetrieve,
} from '../AssignWorkoutModal/utils/templateToWorkoutWrite';
import { lockedHint } from '../WorkoutAssignmentMenu';

interface WorkoutPreviewModalProps {
  opened: boolean;
  onClose: () => void;

  // Pass workoutId to fetch internally (display-only), or previewData for assign mode
  workoutId?: string | null;
  previewData?: WorkoutTemplatePreviewData | null;
  isLoading?: boolean;
  // View to return to after editing from here (defaults to agenda)
  returnView?: 'week' | 'agenda';

  // Assign mode footer — omit for display-only
  onAssign?: (isVisibleToAthletes: boolean) => void;
  assignLoading?: boolean;
  onEditBeforeAssign?: () => void;
  defaultVisibleToAthletes?: boolean;
}

const modalStyles = {
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
    padding: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '80vh',
    maxHeight: '800px',
  },
  header: {
    borderBottom: '1px solid #e9ecef',
  },
};

const WorkoutPreviewModal = ({
  opened,
  onClose,
  workoutId,
  previewData: externalPreviewData,
  isLoading: externalLoading,
  onAssign,
  assignLoading,
  onEditBeforeAssign,
  defaultVisibleToAthletes = true,
  returnView = 'agenda',
}: WorkoutPreviewModalProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');
  const { errorNotification } = notificationHandler();
  const { trackWorkoutVisibilityModalToggled, trackWorkoutEditClicked } = useStaffTracking();
  const [isVisibleToAthletes, setIsVisibleToAthletes] = useState(true);

  const isAssignMode = !!onAssign;

  const { data: workoutResponse, isLoading: workoutFetching } = useWorkout(
    opened && workoutId ? workoutId : undefined,
  );
  const fetchedWorkout = workoutResponse?.data;
  // Only mount the edit mutation in display mode — in assign mode it is never called.
  const editMutation = useEditAssignedWorkout(!isAssignMode ? workoutId ?? undefined : undefined);

  useEffect(() => {
    // In display mode, mirror the persisted value. In assign mode the source's
    // visibility is ignored (DEV-2773 #8).
    if (fetchedWorkout && !isAssignMode) setIsVisibleToAthletes(fetchedWorkout.isVisibleToAthletes);
  }, [fetchedWorkout, isAssignMode]);

  useEffect(() => {
    if (opened && isAssignMode) setIsVisibleToAthletes(defaultVisibleToAthletes);
  }, [opened, isAssignMode, defaultVisibleToAthletes]);

  const isLocked = !isAssignMode && !!fetchedWorkout?.hasResults;
  const lockOwner = fetchedWorkout?.athlete ? 'athlete' : 'classType';

  const resolvedData: WorkoutTemplatePreviewData | null =
    externalPreviewData ?? (fetchedWorkout ? buildPreviewDataFromWorkout(fetchedWorkout) : null);

  const isLoading = externalLoading ?? workoutFetching;

  if (!opened) return <></>;

  const handleVisibilityChange = (next: boolean): void => {
    if (!fetchedWorkout) return;
    if (fetchedWorkout.athlete) {
      trackWorkoutVisibilityModalToggled({
        workout_id: fetchedWorkout.workoutId,
        new_state: next ? 'visible' : 'hidden',
      });
    }
    setIsVisibleToAthletes(next);
    editMutation.mutate(buildWorkoutUpdateFromRetrieve(fetchedWorkout, { isVisibleToAthletes: next }), {
      onError: error => {
        setIsVisibleToAthletes(!next);
        errorNotification('Save failed', handleApiError(error).message);
      },
    });
  };

  const handleEdit = (): void => {
    if (!workoutId) return;
    if (fetchedWorkout?.athlete) trackWorkoutEditClicked({ workout_id: workoutId, source: 'modal' });
    navigate(`/workouts/programming/edit/${workoutId}`, { state: { view: returnView } });
    onClose();
  };

  if (!resolvedData) {
    if (isLoading) {
      return (
        <Modal
          centered
          opened={opened}
          onClose={onClose}
          withCloseButton={false}
          size='xl'
          styles={modalStyles}
        >
          <Stack align='center' py='xl'>
            <Loader />
          </Stack>
        </Modal>
      );
    }
    return <></>;
  }

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size='xl'
      styles={modalStyles}
      title={
        <Stack spacing={4}>
          {onAssign && (
            <UnstyledButton
              onClick={onClose}
              sx={t => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: t.colors.gray[6],
                fontSize: t.fontSizes.sm,
                '&:hover': { color: t.colors.gray[9] },
                marginBottom: t.spacing.xs,
              })}
            >
              <IconChevronLeft size={16} />
              <Text size='sm' inherit>
                Back to list
              </Text>
            </UnstyledButton>
          )}
          <Title order={2}>{resolvedData.name}</Title>
        </Stack>
      }
    >
      <Stack spacing={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          <WorkoutDetail template={resolvedData} />
        </Box>
        <Box p='lg' pt='md' sx={t => ({ flexShrink: 0, borderTop: `1px solid ${t.colors.gray[3]}` })}>
          {isAssignMode ? (
            <Group position='apart' align='center'>
              <Switch
                checked={isVisibleToAthletes}
                onChange={e => setIsVisibleToAthletes(e.currentTarget.checked)}
                label={isVisibleToAthletes ? 'Visible to athletes' : 'Not visible to athletes'}
                color='kiloBlue'
              />
              <Group spacing='sm'>
                {onEditBeforeAssign && (
                  <Button
                    variant='outline'
                    color='dark'
                    leftIcon={<IconEdit size={16} />}
                    onClick={onEditBeforeAssign}
                    disabled={!canMutate}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  color='kiloBlue'
                  onClick={() => onAssign(isVisibleToAthletes)}
                  loading={assignLoading}
                  disabled={!canMutate}
                >
                  Assign
                </Button>
              </Group>
            </Group>
          ) : (
            <Group position='apart' align='center'>
              <Tooltip label={lockedHint(lockOwner)} disabled={!isLocked} withArrow withinPortal>
                <Group spacing='xs' align='center'>
                  <IconEye
                    size={16}
                    color={isVisibleToAthletes ? theme.colors.green[6] : theme.colors.gray[5]}
                  />
                  <Text size='sm'>Visible to athletes</Text>
                  <Switch
                    checked={isVisibleToAthletes}
                    onChange={e => handleVisibilityChange(e.currentTarget.checked)}
                    disabled={editMutation.isLoading || !canMutate || isLocked}
                    color='kiloBlue'
                  />
                </Group>
              </Tooltip>
              <Group spacing='sm'>
                {canMutate && (
                  <Tooltip label={lockedHint(lockOwner)} disabled={!isLocked} withArrow withinPortal>
                    <Box component='span'>
                      <Button
                        variant='default'
                        leftIcon={<IconEdit size={16} />}
                        onClick={handleEdit}
                        disabled={isLocked}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Tooltip>
                )}
                <Button variant='default' onClick={onClose}>
                  Close
                </Button>
              </Group>
            </Group>
          )}
        </Box>
      </Stack>
    </Modal>
  );
};

export default WorkoutPreviewModal;
